/**
 * Kiosk helpers
 *
 * Simple explanation: Small, reusable functions used by kiosk components
 * for filtering stations, formatting text, validating map selections, etc.
 *
 * Technical summary:
 * - Filtering/searching across station rows
 * - Map config validation and display formatting
 * - Basic numeric/text utilities for inputs and labels
 */

/**
 * Extracts unique values from an array based on a specified key
 * @param {Array} arr - The array to process
 * @param {string} key - The key to extract unique values from
 * @returns {Array} Array of unique values
 */
export const getUnique = (arr, key) => [
  ...new Set(arr.map((item) => item[key] || "Unknown")),
];

/**
 * Safely converts values to strings for DynamoDB storage
 * Handles null, undefined, and other edge cases
 * @param {*} value - The value to convert
 * @param {string} defaultValue - Default value if conversion fails
 * @returns {string} String representation of the value
 */
export const safeToString = (value, defaultValue = "0") => {
  if (value === null || value === undefined) return defaultValue;
  return value.toString();
};

/**
 * Filters rows based on search term and filter criteria
 * @param {Array} rows - Array of station rows to filter
 * @param {Object} filters - Filter criteria object
 * @param {string} search - Search term
 * @returns {Array} Filtered rows
 */
export const filterRows = (rows, filters, search) => {
  return rows.filter((row) => {
    // Search filter - check name, ID, and community
    const searchLower = search.toLowerCase();
    const matchesSearch =
      row.name.toLowerCase().includes(searchLower) ||
      row.id.toLowerCase().includes(searchLower) ||
      (row.Community || "").toLowerCase().includes(searchLower);

    // Apply dropdown filters
    const matchesStatus = !filters.status || row.status === filters.status;
    const matchesType = !filters.type || row.type === filters.type;
    const matchesCommunity =
      !filters.Community || row.Community === filters.Community;

    return matchesSearch && matchesStatus && matchesType && matchesCommunity;
  });
};

/**
 * Validates map configuration object
 * @param {Object} mapConfig - Map configuration object
 * @returns {boolean} True if configuration is valid
 */
export const isValidMapConfig = (mapConfig) => {
  return (
    mapConfig &&
    mapConfig.center &&
    Array.isArray(mapConfig.center) &&
    mapConfig.center.length === 2 &&
    mapConfig.zoom !== null &&
    mapConfig.zoom !== undefined
  );
};

/**
 * Formats coordinates for display
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @returns {string} Formatted coordinate string
 */
export const formatCoordinates = (latitude, longitude) => {
  return `${latitude}, ${longitude}`;
};

/**
 * Creates a station display name with ID
 * @param {string} name - Station name
 * @param {string} id - Station ID
 * @returns {string} Formatted station name with ID
 */
export const formatStationName = (name, id) => {
  return `${name} (${id})`;
};

/**
 * Handles timer input validation and formatting
 * @param {string} value - Input value
 * @returns {string} Cleaned numeric string
 */
export const sanitizeTimerInput = (value) => {
  return value.replace(/[^0-9]/g, "");
};

/**
 * Determines if a station should be displayed based on its status
 * @param {string} status - Station status
 * @returns {boolean} True if station should be displayed
 */
export const shouldDisplayStation = (status) => {
  return status === "Operational" || status === "Warning";
};

/**
 * Calculates the approximate area covered by map bounds
 * @param {Array} bounds - Map bounds array [[sw_lat, sw_lng], [ne_lat, ne_lng]]
 * @returns {string} Formatted area in km²
 */
export const calculateMapArea = (bounds) => {
  if (!bounds || !Array.isArray(bounds) || bounds.length !== 2) {
    return null;
  }

  const latDiff = Math.abs(bounds[1][0] - bounds[0][0]);
  const lngDiff = Math.abs(bounds[1][1] - bounds[0][1]);

  // Approximate area calculation (very simplified):
  // 1 degree ≈ 111 km at the equator, multiply lat/long spans
  const area = latDiff * lngDiff * 111 * 111;
  return area.toFixed(1);
};

/**
 * Formats map configuration for display
 * @param {Object} mapConfig - Map configuration object
 * @returns {Object} Formatted display object
 */
export const formatMapConfigForDisplay = (mapConfig) => {
  if (!isValidMapConfig(mapConfig)) {
    return null;
  }

  return {
    center: `${mapConfig.center[0].toFixed(4)}, ${mapConfig.center[1].toFixed(
      4
    )}`,
    zoom: `Level ${mapConfig.zoom}`,
    bounds: mapConfig.bounds
      ? {
          southwest: `${mapConfig.bounds[0][0].toFixed(
            4
          )}, ${mapConfig.bounds[0][1].toFixed(4)}`,
          northeast: `${mapConfig.bounds[1][0].toFixed(
            4
          )}, ${mapConfig.bounds[1][1].toFixed(4)}`,
        }
      : null,
    area: mapConfig.bounds ? calculateMapArea(mapConfig.bounds) : null,
  };
};
